import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import '../styles/FindMinyan.css';

const API = config.API_URL || '/api';
const APP_STORE_URL  = 'https://apps.apple.com/us/app/yesod/id6748472269';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=us.yesod.app';

// ─── Prayer type config ──────────────────────────────────────────────────────
const PRAYER = {
  shacharit: { label: 'Shacharit', tag: 'Morning',   color: '#f59e0b', bg: '#fffbeb' },
  mincha:    { label: 'Mincha',    tag: 'Afternoon', color: '#0ea5e9', bg: '#f0f9ff' },
  maariv:    { label: 'Maariv',    tag: 'Evening',   color: '#6366f1', bg: '#eef2ff' },
};
const prayerCfg = (type) => PRAYER[type] || { label: 'Prayer', tag: '', color: '#9ca3af', bg: '#f9fafb' };

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'shacharit', label: 'Shacharit' },
  { key: 'mincha',    label: 'Mincha' },
  { key: 'maariv',    label: 'Maariv' },
];

const DAY_NAMES   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const POPULAR_AREAS = [
  { city: 'New York',    state: 'NY', lat: 40.7128, lng: -74.0060 },
  { city: 'Miami',       state: 'FL', lat: 25.7617, lng: -80.1918 },
  { city: 'Los Angeles', state: 'CA', lat: 34.0522, lng: -118.2437 },
  { city: 'Baltimore',   state: 'MD', lat: 39.2904, lng: -76.6122  },
  { city: 'Chicago',     state: 'IL', lat: 41.8781, lng: -87.6298  },
  { city: 'Boca Raton',  state: 'FL', lat: 26.3587, lng: -80.0831  },
];

// ─── Time utilities ───────────────────────────────────────────────────────────
const parseTo24h = (t) => {
  if (!t) return '';
  const s = t.trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':');
    return `${String(parseInt(h, 10)).padStart(2, '0')}:${m}`;
  }
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const p = m12[3].toLowerCase();
    if (p === 'pm' && h !== 12) h += 12;
    if (p === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${m12[2]}`;
  }
  return s;
};

const formatDisplay = (t) => {
  const s = parseTo24h(t);
  if (!s || !s.includes(':')) return t || '';
  const [h24, m] = s.split(':');
  const h = parseInt(h24, 10);
  const period = h >= 12 ? 'PM' : 'AM';
  const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${dh}:${m} ${period}`;
};

const nowMinutes = () => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
};

const timeToMinutes = (t) => {
  const s = parseTo24h(t);
  if (!s || !s.includes(':')) return null;
  const [h, m] = s.split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
};

// Returns { type: 'now'|'soon'|'upcoming'|'later'|'past', label: string|null }
const getStatus = (timeStr) => {
  const svcMins = timeToMinutes(timeStr);
  if (svcMins === null) return null;
  const diff = svcMins - nowMinutes(); // positive = future
  if (diff >= -20 && diff <= 0)   return { type: 'now',      label: 'Now' };
  if (diff > 0  && diff <= 15)    return { type: 'soon',     label: `In ${diff} min` };
  if (diff > 15 && diff <= 90)    return { type: 'upcoming', label: `In ${Math.round(diff / 5) * 5} min` };
  if (diff > 90)                  return { type: 'later',    label: null };
  return { type: 'past', label: null };
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function DirectionsBtn({ item, size = 'normal' }) {
  const open = () => {
    const q = item.address
      ? encodeURIComponent(`${item.address}, ${item.city || ''} ${item.state || ''}`.trim())
      : encodeURIComponent(`${item.group_name} ${item.city || ''} ${item.state || ''}`.trim());
    window.open(`https://maps.google.com/?q=${q}`, '_blank', 'noopener');
  };
  return (
    <button
      className={`fm-dir-btn${size === 'small' ? ' fm-row__expand-dir' : ''}`}
      onClick={(e) => { e.stopPropagation(); open(); }}
    >
      Directions
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    </button>
  );
}

// ─── Share a minyan (native share sheet → clipboard fallback) ─────────────────
function ShareBtn({ item, variant = 'drawer' }) {
  const [copied, setCopied] = useState(false);

  const handleShare = (e) => {
    e.stopPropagation();
    const params = new URLSearchParams({
      t: item.time        || '',
      p: item.prayer_type || '',
      n: item.group_name  || '',
      c: item.city        || '',
      s: item.state       || '',
      a: item.address     || '',
    });
    const url  = `${window.location.origin}${window.location.pathname}#/minyan-share?${params.toString()}`;
    const cfg  = prayerCfg(item.prayer_type);
    const text = `${cfg.label} at ${formatDisplay(item.time)} – ${item.group_name}${item.city ? `, ${item.city}` : ''}`;
    if (navigator.share) {
      navigator.share({ title: 'Minyan time', text, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      }).catch(() => {});
    }
  };

  const ShareIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );

  if (variant === 'hero') {
    return (
      <button className="fm-hero-share-btn" onClick={handleShare} aria-label="Share this minyan">
        <ShareIcon />
        {copied ? 'Copied!' : 'Share'}
      </button>
    );
  }
  return (
    <button className="fm-row__expand-share" onClick={handleShare} aria-label="Share">
      <ShareIcon />
      {copied ? 'Copied!' : 'Share'}
    </button>
  );
}

// ─── Save (gates to app download) ────────────────────────────────────────────
function SaveBtn({ variant = 'drawer', onSave }) {
  const handle = (e) => { e.stopPropagation(); onSave(); };

  const BookmarkIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
    </svg>
  );

  if (variant === 'hero') {
    return (
      <button className="fm-hero-save-btn" onClick={handle} aria-label="Save this minyan">
        <BookmarkIcon />
        Save
      </button>
    );
  }
  return (
    <button className="fm-row__expand-save" onClick={handle} aria-label="Save">
      <BookmarkIcon />
      Save
    </button>
  );
}

// ─── App gate modal (bottom sheet) ───────────────────────────────────────────
function AppGateModal({ onClose }) {
  return (
    <div className="fm-gate-overlay" onClick={onClose}
      role="dialog" aria-modal="true" aria-label="Get the Yesod app">
      <div className="fm-gate-modal" onClick={e => e.stopPropagation()}>
        <div className="fm-gate-pill" />
        <button className="fm-gate-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
        <p className="fm-gate-icon">🔔</p>
        <h2 className="fm-gate-title">Save &amp; Get Reminders</h2>
        <p className="fm-gate-sub">
          Download the Yesod app to save minyan times, set reminders before davening,
          and get notified when times change.
        </p>
        <ul className="fm-gate-features">
          <li>Save favorite minyan times</li>
          <li>Push reminder before minyan</li>
          <li>Alerts when times change</li>
        </ul>
        <div className="fm-gate-stores">
          <button className="fm-gate-store-btn"
            onClick={() => window.open(APP_STORE_URL, '_blank', 'noopener')}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"/>
            </svg>
            App Store
          </button>
          <button className="fm-gate-store-btn fm-gate-store-btn--play"
            onClick={() => window.open(PLAY_STORE_URL, '_blank', 'noopener')}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path d="M3.18 23.77c.26.15.55.23.84.23.28 0 .56-.07.82-.21l10.96-6.35-2.62-2.62-10 9.95zm-1.18-21c-.01.1-.01.2-.01.3V20.9c0 .11 0 .21.01.32l10.32-10.32L2 2.77zm19.53 8.93L19.73 10l-2.33-1.35L14.83 12l2.57 2.57 2.39-1.38c.68-.41.97-1.08.74-1.49zM4.01.44l10 9.95 2.62-2.62L5.67.42c-.52-.3-1.17-.25-1.66.02z"/>
            </svg>
            Google Play
          </button>
        </div>
      </div>
    </div>
  );
}

function HeroCard({ item, onSave }) {
  const cfg    = prayerCfg(item.prayer_type);
  const status = getStatus(item.time);

  return (
    <div className="fm-hero-card">
      <div className="fm-hero-card__stripe" style={{ background: cfg.color }} />
      <div className="fm-hero-card__content">
        {/* Top row */}
        <div className="fm-hero-card__top-row">
          <p className="fm-hero-card__eyebrow">Next Minyan</p>
          {status && status.type !== 'later' && status.type !== 'past' && (
            <span className={`fm-badge fm-badge--${status.type}`}>
              {status.type === 'now' ? '● Live' : status.label}
            </span>
          )}
        </div>

        {/* Time — the hero */}
        <p className="fm-hero-card__time">{formatDisplay(item.time)}</p>

        {/* Prayer type */}
        <p className="fm-hero-card__prayer" style={{ color: cfg.color }}>
          {cfg.label}
        </p>

        <div className="fm-hero-card__divider" />

        {/* Synagogue info */}
        <p className="fm-hero-card__group">{item.group_name}</p>
        <div className="fm-hero-card__meta">
          {item.address && <span>{item.address}</span>}
          {item.address && (item.city || item.distance_miles != null) && (
            <span className="fm-hero-card__meta-dot">·</span>
          )}
          {item.city && <span>{item.city}{item.state ? `, ${item.state}` : ''}</span>}
          {item.distance_miles != null && (
            <>
              {item.city && <span className="fm-hero-card__meta-dot">·</span>}
              <span>{item.distance_miles} mi away</span>
            </>
          )}
        </div>

        <div className="fm-hero-card__actions">
          <DirectionsBtn item={item} />
          <ShareBtn item={item} variant="hero" />
          <SaveBtn variant="hero" onSave={onSave} />
        </div>
      </div>
    </div>
  );
}

function MinyanRow({ item, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const cfg    = prayerCfg(item.prayer_type);
  const status = getStatus(item.time);
  const isPast = status?.type === 'past';

  return (
    <li
      className={[
        'fm-row',
        isPast      ? 'fm-row--past'     : '',
        expanded    ? 'fm-row--expanded' : '',
      ].filter(Boolean).join(' ')}
      onClick={() => !isPast && setExpanded(e => !e)}
      role="button"
      tabIndex={isPast ? -1 : 0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !isPast && setExpanded(v => !v); }}}
      aria-expanded={expanded}
    >
      {/* Time column */}
      <div className="fm-row__time-wrap">
        <span className="fm-row__time">{formatDisplay(item.time)}</span>
        {status && !isPast && status.label && (
          <span className={`fm-row__time-badge fm-row__time-badge--${status.type}`}>
            {status.label}
          </span>
        )}
      </div>

      {/* Prayer type dot */}
      <div className="fm-row__dot" style={{ background: cfg.color }} />

      {/* Info */}
      <div className="fm-row__info">
        <p className="fm-row__type">{cfg.label}</p>
        <p className="fm-row__group">
          {item.group_name}
          {item.city ? ` · ${item.city}` : ''}
        </p>
      </div>

      {/* Right: distance + chevron */}
      <div className="fm-row__right">
        {item.distance_miles != null && (
          <span className="fm-row__dist">{item.distance_miles} mi</span>
        )}
        <svg className="fm-row__chevron" viewBox="0 0 24 24" width="14" height="14"
          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
          <path d={expanded ? 'M18 15l-6-6-6 6' : 'M9 18l6-6-6-6'} />
        </svg>
      </div>

      {/* Expanded drawer */}
      {expanded && (
        <div className="fm-row__expand" onClick={e => e.stopPropagation()}>
          <p className="fm-row__expand-addr">
            {[item.address, item.city, item.state].filter(Boolean).join(', ') || 'Address unavailable'}
          </p>
          <div className="fm-row__expand-actions">
            <ShareBtn item={item} />
            <SaveBtn onSave={onSave} />
            <DirectionsBtn item={item} size="small" />
          </div>
        </div>
      )}
    </li>
  );
}

function SkeletonLoader() {
  return (
    <>
      <div className="fm-hero-section">
        <div className="fm-skeleton-hero" />
      </div>
      <div className="fm-list" style={{ marginTop: '1rem' }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="fm-skeleton-row" />
        ))}
      </div>
    </>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function FindMinyanPage() {
  const navigate = useNavigate();

  // Location state
  const [locState, setLocState] = useState('idle'); // idle | locating | located | denied | city
  const [coords, setCoords]     = useState(null);   // { lat, lng }
  const [locLabel, setLocLabel] = useState('');
  const [cityInput, setCityInput] = useState('');

  // Data state
  const [minyanim, setMinyanim]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // UI state
  const [filter, setFilter]       = useState('all');
  const [showTomorrow, setShowTomorrow] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showGate, setShowGate]     = useState(false);
  const cityInputRef = useRef(null);
  const searchTimer  = useRef(null);

  // Search state (when location denied)
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Derived: group search results by city for area-level rows
  const cityGroups = useMemo(() => {
    if (!searchResults.length) return [];
    const map = {};
    searchResults.forEach(g => {
      if (!g.city && !g.state) return;
      const key = `${(g.city || '').toLowerCase()}__${(g.state || '').toLowerCase()}`;
      if (!map[key]) map[key] = { city: g.city || '', state: g.state || '', shuls: [] };
      map[key].shuls.push(g);
    });
    return Object.values(map)
      .sort((a, b) => b.shuls.length - a.shuls.length)
      .slice(0, 5);
  }, [searchResults]);
  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchMinyanim = useCallback(async ({ lat, lng, day } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ filter });
      if (lat != null) { params.set('lat', lat); params.set('lng', lng); }
      if (day  != null) params.set('day', day);
      const res = await fetch(`${API}/public/minyanim?${params}`);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      setMinyanim(data.minyanim || []);
    } catch (err) {
      console.error('fetchMinyanim:', err);
      setError('Could not load minyanim. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // ── Group search (used when location is denied) ────────────────────────────
  const searchGroups = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res  = await fetch(`${API}/public/groups/city-search?q=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setSearchResults(data.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleGroupSelect = useCallback((group) => {
    if (!group.latitude || !group.longitude) return;
    setCoords({ lat: group.latitude, lng: group.longitude });
    setLocLabel(`${group.name}${group.city ? ` · ${group.city}` : ''}`);
    setLocState('located');
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    fetchMinyanim({ lat: group.latitude, lng: group.longitude });
  }, [fetchMinyanim]);

  // City-area selection: centroid of all shuls in that city
  const handleCitySelect = useCallback((cityGroup) => {
    const withCoords = cityGroup.shuls.filter(g => g.latitude && g.longitude);
    if (!withCoords.length) return;
    const lat = withCoords.reduce((s, g) => s + parseFloat(g.latitude),  0) / withCoords.length;
    const lng = withCoords.reduce((s, g) => s + parseFloat(g.longitude), 0) / withCoords.length;
    setCoords({ lat, lng });
    setLocLabel(`${cityGroup.city}${cityGroup.state ? `, ${cityGroup.state}` : ''} area`);
    setLocState('located');
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    fetchMinyanim({ lat, lng });
  }, [fetchMinyanim]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchGroups(q), 300);
  };

  // ── Location ───────────────────────────────────────────────────────────────
  const requestGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setLocState('denied');
      return;
    }
    setLocState('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        setLocLabel('Your location');
        setLocState('located');
        setShowSearch(false);
        fetchMinyanim({ lat, lng });
        // Resolve city name in the background
        fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`)
          .then(r => r.ok ? r.json() : null)
          .then(d => {
            const city = d?.city || d?.locality || d?.principalSubdivision || null;
            if (city) setLocLabel(city);
          })
          .catch(() => {});
      },
      () => {
        setLocState('denied');
      },
      { timeout: 8000, enableHighAccuracy: false }
    );
  }, [fetchMinyanim]);

  // Auto-request GPS on mount
  useEffect(() => { requestGPS(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // (search panel opens only when user explicitly taps Change / Search)

  // Re-fetch when filter or tomorrow toggle changes
  useEffect(() => {
    if (locState === 'idle' || locState === 'locating' || locState === 'denied') return;
    const day = showTomorrow ? (new Date().getDay() + 1) % 7 : undefined;
    fetchMinyanim({ lat: coords?.lat, lng: coords?.lng, day });
  }, [filter, showTomorrow]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived data ───────────────────────────────────────────────────────────
  const now = nowMinutes();

  // Split into: upcoming (including "now"), and already happened
  const upcoming = minyanim.filter(m => {
    const mins = timeToMinutes(m.time);
    return mins === null || mins >= now - 20; // include "now" window
  });
  const pastToday = minyanim.filter(m => {
    const mins = timeToMinutes(m.time);
    return mins !== null && mins < now - 20;
  });

  // The "next" minyan is the first item in upcoming that hasn't ended
  const nextMinyan = upcoming.find(m => {
    const s = getStatus(m.time);
    return s && (s.type === 'now' || s.type === 'soon' || s.type === 'upcoming' || s.type === 'later');
  }) || upcoming[0] || null;

  // List = everything except the hero (to avoid duplication), past at the bottom
  const listItems = [
    ...upcoming.filter(m => m !== nextMinyan),
    ...pastToday,
  ];

  const hasNoMoreToday = !loading && upcoming.length === 0 && !showTomorrow;

  // ── Location display label ──────────────────────────────────────────────────
  const displayLabel = locLabel || (locState === 'locating' ? 'Detecting location…' : locState === 'denied' ? 'Location disabled' : 'All locations');
  const today        = new Date();
  const displayDay   = showTomorrow
    ? `${DAY_NAMES[(today.getDay() + 1) % 7]}, ${MONTH_NAMES[today.getMonth()]} ${today.getDate() + 1}`
    : `${DAY_NAMES[today.getDay()]}, ${MONTH_NAMES[today.getMonth()]} ${today.getDate()}`;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fm">

      {/* ── Nav ──────────────────────────────────────────────────────────── */}
      <header className="fm-nav">
        <button
          className="fm-nav__back"
          aria-label="Go back"
          onClick={() => {
            if (locState === 'located' || locState === 'city') {
              // Reset to the explore view instead of leaving the page
              setLocState('denied');
              setCoords(null);
              setLocLabel('');
              setMinyanim([]);
              setError(null);
              setShowSearch(false);
            } else {
              navigate(-1);
            }
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
          Back
        </button>
        <h1 className="fm-nav__title">Find a Minyan</h1>
        <div className="fm-nav__spacer" />
      </header>

      {/* ── Location bar ─────────────────────────────────────────────────── */}
      <div className="fm-loc-bar">
        <svg className="fm-loc-bar__icon" viewBox="0 0 24 24" width="16" height="16"
          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
          <circle cx="12" cy="9" r="2.5"/>
        </svg>
        <div>
          <p className="fm-loc-bar__text">{displayLabel}</p>
        </div>
        {locState !== 'locating' && (
          <button className="fm-loc-bar__change" onClick={() => setShowSearch(true)}>
            {locState === 'denied' ? 'Search' : 'Change'}
          </button>
        )}
      </div>

      <main className="fm-main">

        {/* ── Default explore view (before location is chosen) ──────────── */}
        {(locState === 'idle' || locState === 'denied') && !showSearch && (
          <div className="fm-idle">

            {/* Use my location — prominent row */}
            <button className="fm-idle__gps-btn" onClick={requestGPS}>
              <div className="fm-idle__gps-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                </svg>
              </div>
              <div className="fm-idle__gps-text">
                <span className="fm-idle__gps-label">Use my current location</span>
                <span className="fm-idle__gps-sub">Find minyanim nearest to you</span>
              </div>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>

            {/* Divider */}
            <div className="fm-idle__divider"><span>or explore a city</span></div>

            {/* Quick chips */}
            <div className="fm-idle__chips">
              {POPULAR_AREAS.map(a => (
                <button
                  key={a.city}
                  className="fm-idle__chip"
                  onClick={() => {
                    setCoords({ lat: a.lat, lng: a.lng });
                    setLocLabel(`${a.city}, ${a.state}`);
                    setLocState('located');
                    fetchMinyanim({ lat: a.lat, lng: a.lng });
                  }}>
                  {a.city}
                </button>
              ))}
            </div>

            {/* Popular area cards */}
            <p className="fm-search-section-label">Popular areas</p>
            {POPULAR_AREAS.map(a => (
              <button
                key={a.city}
                className="fm-search-city-item"
                onClick={() => {
                  setCoords({ lat: a.lat, lng: a.lng });
                  setLocLabel(`${a.city}, ${a.state}`);
                  setLocState('located');
                  fetchMinyanim({ lat: a.lat, lng: a.lng });
                }}>
                <div className="fm-search-city-item__icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5"/>
                  </svg>
                </div>
                <div className="fm-search-city-item__body">
                  <p className="fm-search-city-item__name">{a.city}, {a.state}</p>
                  <p className="fm-search-city-item__sub">Browse minyanim in this area</p>
                </div>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                  stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            ))}

          </div>
        )}

        {/* ── Full-screen search overlay (Google Maps style) ───────────────── */}
        {showSearch && (
          <div className="fm-search-overlay">

            {/* Header / search bar */}
            <div className="fm-search-overlay__bar">
              {locState !== 'denied' && (
                <button className="fm-search-overlay__back"
                  onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}
                  aria-label="Close search">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M19 12H5M12 5l-7 7 7 7"/>
                  </svg>
                </button>
              )}
              <div className="fm-search-overlay__wrap">
                {searchLoading
                  ? <div className="fm-search-overlay__spinner" aria-hidden="true" />
                  : (
                    <svg className="fm-search-overlay__icon" viewBox="0 0 24 24" width="18" height="18"
                      fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="M21 21l-4.35-4.35"/>
                    </svg>
                  )
                }
                <input
                  ref={cityInputRef}
                  type="search"
                  className="fm-search-overlay__input"
                  placeholder="Search city, shul name, or address…"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  autoComplete="off"
                  autoFocus
                />
                {searchQuery && (
                  <button className="fm-search-overlay__clear"
                    onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                    aria-label="Clear">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
                      stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                )}
              </div>
              {locState !== 'denied' && (
                <button className="fm-search-overlay__cancel"
                  onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}>
                  Cancel
                </button>
              )}
            </div>

            {/* Results body */}
            <div className="fm-search-overlay__body">

              {/* GPS row — always present */}
              <button className="fm-search-gps-row" onClick={requestGPS}>
                <div className="fm-search-gps-row__icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                  </svg>
                </div>
                <div className="fm-search-gps-row__text">
                  <span className="fm-search-gps-row__label">Use my current location</span>
                  {locState === 'denied' && (
                    <span className="fm-search-gps-row__sub">Tap to enable location access</span>
                  )}
                </div>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                  stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>

              {/* Empty hint */}
              {!searchQuery.trim() && (
                <p className="fm-search-section-label">Search by city, zip code, or synagogue name</p>
              )}

              {/* ── City / area rows ───────────────────────────────────────── */}
              {cityGroups.length > 0 && (
                <>
                  <p className="fm-search-section-label">Areas</p>
                  {cityGroups.map(cg => (
                    <button
                      key={`${cg.city}-${cg.state}`}
                      className="fm-search-city-item"
                      onClick={() => handleCitySelect(cg)}>
                      <div className="fm-search-city-item__icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                          <circle cx="12" cy="9" r="2.5"/>
                        </svg>
                      </div>
                      <div className="fm-search-city-item__body">
                        <p className="fm-search-city-item__name">
                          {cg.city}{cg.state ? `, ${cg.state}` : ''}
                        </p>
                        <p className="fm-search-city-item__sub">
                          {cg.shuls.length} synagogue{cg.shuls.length !== 1 ? 's' : ''} · see all minyanim
                        </p>
                      </div>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                        stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </button>
                  ))}
                </>
              )}

              {/* ── Individual synagogue rows ────────────────────────────────── */}
              {searchResults.length > 0 && (
                <>
                  {cityGroups.length > 0 && (
                    <p className="fm-search-section-label">Synagogues</p>
                  )}
                  <ul className="fm-search-result-list">
                    {searchResults.map(g => (
                      <li key={g.id} className="fm-search-result-item"
                        onClick={() => handleGroupSelect(g)}
                        role="button" tabIndex={0}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleGroupSelect(g); }}>
                        <div className="fm-search-result-item__avatar">
                          {g.logo_url
                            ? <img src={g.logo_url} alt="" />
                            : <span>{(g.name || '?')[0].toUpperCase()}</span>
                          }
                        </div>
                        <div className="fm-search-result-item__body">
                          <p className="fm-search-result-item__name">{g.name}</p>
                          {g.address && (
                            <p className="fm-search-result-item__addr">{g.address}</p>
                          )}
                          <p className="fm-search-result-item__meta">
                            {[g.city, g.state, g.zip_code].filter(Boolean).join(', ')}
                          </p>
                        </div>
                        <svg className="fm-search-result-item__arrow" viewBox="0 0 24 24" width="14" height="14"
                          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* No results */}
              {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <div className="fm-search-empty">
                  <p className="fm-search-empty__title">No results for “{searchQuery}”</p>
                  <p className="fm-search-empty__sub">Try a city name, zip code, or synagogue name</p>
                </div>
              )}

            </div>
          </div>
        )}

        {/* ── Loading state ──────────────────────────────────────────────── */}
        {loading && <SkeletonLoader />}

        {/* ── Error state ───────────────────────────────────────────────── */}
        {error && !loading && (
          <div className="fm-empty">
            <div className="fm-empty__icon">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
            </div>
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button className="fm-empty__btn"
              onClick={() => fetchMinyanim({ lat: coords?.lat, lng: coords?.lng })}>
              Try again
            </button>
          </div>
        )}

        {/* ── Main content ───────────────────────────────────────────────── */}
        {!loading && !error && locState !== 'idle' && locState !== 'denied' && (
          <>
            {/* Controls */}
            <div className="fm-controls">
              <span className="fm-day-label">{displayDay}</span>
              <div className="fm-filter-chips">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    className={`fm-chip${filter === f.key ? ' fm-chip--active' : ''}`}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Hero: next minyan */}
            {nextMinyan && (
              <section className="fm-hero-section">
                <HeroCard item={nextMinyan} onSave={() => setShowGate(true)} />
              </section>
            )}

            {/* No more minyanim today → prompt for tomorrow */}
            {hasNoMoreToday && (
              <div className="fm-tomorrow-bar">
                <p className="fm-tomorrow-bar__text">No more minyanim today</p>
                <button className="fm-tomorrow-bar__btn"
                  onClick={() => setShowTomorrow(true)}>
                  See tomorrow →
                </button>
              </div>
            )}

            {/* Full schedule list */}
            {listItems.length > 0 && (
              <>
                <p className="fm-schedule-title">
                  {showTomorrow ? 'Tomorrow' : "Today's schedule"}
                  {minyanim.length > 0 && ` · ${minyanim.length} ${minyanim.length === 1 ? 'minyan' : 'minyanim'}`}
                </p>

                {/* Upcoming section */}
                {upcoming.filter(m => m !== nextMinyan).length > 0 && (
                  <ul className="fm-list">
                    {upcoming.filter(m => m !== nextMinyan).map(m => (
                      <MinyanRow key={m.id} item={m} onSave={() => setShowGate(true)} />
                    ))}
                  </ul>
                )}

                {/* Past section */}
                {pastToday.length > 0 && (
                  <>
                    <div className="fm-section-divider">
                      <span className="fm-section-divider__label">Earlier today</span>
                      <div className="fm-section-divider__line" />
                    </div>
                    <ul className="fm-list">
                      {pastToday.map(m => (
                        <MinyanRow key={m.id} item={m} onSave={() => setShowGate(true)} />
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}

            {/* True empty state */}
            {minyanim.length === 0 && !loading && (
              <div className="fm-empty">
                <div className="fm-empty__icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                    stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5"/>
                  </svg>
                </div>
                <h3>No minyanim found nearby</h3>
                <p>
                  {coords
                    ? 'We couldn\'t find any minyanim within 25 miles. Try browsing all.'
                    : 'No minyanim have been listed for today yet.'}
                </p>
                {coords && (
                  <button className="fm-empty__btn"
                    onClick={() => fetchMinyanim({})}>
                    Browse all locations
                  </button>
                )}
              </div>
            )}

            {/* App banner */}
            {minyanim.length > 0 && (
              <div className="fm-app-banner">
                <div className="fm-app-banner__copy">
                  <h3>Get daily minyan reminders</h3>
                  <p>Save your favourites and never miss a minyan</p>
                </div>
                <div className="fm-app-banner__btns">
                  <button className="fm-app-banner__store"
                    onClick={() => window.open(APP_STORE_URL, '_blank', 'noopener')}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"/>
                    </svg>
                    App Store
                  </button>
                  <button className="fm-app-banner__store"
                    onClick={() => window.open(PLAY_STORE_URL, '_blank', 'noopener')}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
                      <path d="M3.18 23.77c.26.15.55.23.84.23.28 0 .56-.07.82-.21l10.96-6.35-2.62-2.62-10 9.95zm-1.18-21c-.01.1-.01.2-.01.3V20.9c0 .11 0 .21.01.32l10.32-10.32L2 2.77zm19.53 8.93L19.73 10l-2.33-1.35L14.83 12l2.57 2.57 2.39-1.38c.68-.41.97-1.08.74-1.49zM4.01.44l10 9.95 2.62-2.62L5.67.42c-.52-.3-1.17-.25-1.66.02z"/>
                    </svg>
                    Google Play
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* ── App gate modal (triggered by Save buttons) ──────── */}
      {showGate && <AppGateModal onClose={() => setShowGate(false)} />}

    </div>
  );
}
